import { useState } from 'react';
import {
  useZohoSyncState,
  useZohoWebhookEvents,
  useTriggerZohoSync,
  zohoPullTaskFor,
  type ZohoModule,
  type ZohoSyncStateRow,
  type ZohoWebhookEventRow,
} from '../hooks/useCustomerProvisioning';
import { describeJobError } from '../lib/triggerTask';
import { formatDateTime } from '../lib/formatters';

function redactCrmIdentifiers(message: string): string {
  return message
    .replace(/ZOHO-\d+/gi, 'CRM record')
    .replace(/\b\d{12,}\b/g, 'CRM record');
}

type Feedback = { tone: 'success' | 'error'; text: string };

const BTN =
  'inline-flex items-center justify-center min-h-[44px] md:min-h-[36px] px-3 rounded-md text-xs font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50';

/**
 * Zoho sync status.
 *
 *   • zoho_sync_state — per-module pull/push cursor + last status
 *   • zoho_webhook_events — append-only ingest log with per-row errors
 *
 * Webhooks are primary; the 15-minute pull is the fallback. Pulls are
 * queued through the `trigger-task` Edge Function; the outcome of every
 * request is shown next to the button that queued it.
 */
export function ZohoSyncPage() {
  const { data: states, isLoading, isError, error } = useZohoSyncState();
  const trigger = useTriggerZohoSync();
  const failed = useZohoWebhookEvents({ onlyErrors: true });
  const unprocessed = useZohoWebhookEvents({ onlyUnprocessed: true });
  const recent = useZohoWebhookEvents();
  const [feedback, setFeedback] = useState<Record<string, Feedback>>({});

  const pull = async (module: ZohoModule, key: string = module) => {
    setFeedback((f) => {
      const next = { ...f };
      delete next[key];
      return next;
    });
    try {
      const result = await trigger.mutateAsync(module);
      setFeedback((f) => ({
        ...f,
        [key]: { tone: 'success', text: `${module} pull queued${result.runId ? ` (run ${result.runId})` : ''}. This page refreshes every 30 seconds.` },
      }));
    } catch (e) {
      setFeedback((f) => ({ ...f, [key]: { tone: 'error', text: describeJobError(e) } }));
    }
  };

  const runFirstSync = async () => {
    await pull('Accounts', 'first');
    await pull('Contacts', 'first-contacts');
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-text">Zoho Sync</h1>
        <p className="text-sm text-text-muted mt-1">
          Zoho CRM is the master for customers and contacts. Webhooks deliver changes as they happen; the scheduled pull every 15 minutes catches anything missed.
        </p>
      </div>

      {isLoading ? (
        <div className="rounded-lg border border-border bg-surface p-6 text-sm text-text-muted" role="status">Loading sync state…</div>
      ) : isError ? (
        <div role="alert" className="rounded-lg border border-border bg-surface p-6 text-sm text-notification break-words">
          Could not load the sync state{error instanceof Error && error.message ? `: ${error.message}` : ''}. Refresh to try again.
        </div>
      ) : (states ?? []).length === 0 ? (
        <div className="rounded-lg border border-border bg-surface p-6 space-y-3">
          <div>
            <h2 className="text-sm font-semibold text-text">No sync has run yet</h2>
            <p className="text-sm text-text-muted mt-1">
              <code className="font-mono">zoho_sync_state</code> is empty: neither the scheduled pull nor a webhook has written a cursor. Queue the first Accounts and Contacts pull to populate customers and contacts from Zoho.
            </p>
          </div>
          <button type="button" onClick={runFirstSync} disabled={trigger.isPending} className={`${BTN} bg-primary text-text-inverse hover:bg-primary-hover`}>
            {trigger.isPending ? 'Queuing…' : 'Run first sync'}
          </button>
          {feedback.first && <FeedbackLine feedback={feedback.first} />}
          {feedback['first-contacts'] && <FeedbackLine feedback={feedback['first-contacts']} />}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(states ?? []).map((s) => (
            <ModuleCard
              key={s.module}
              state={s}
              onSync={() => pull(s.module)}
              syncing={trigger.isPending && trigger.variables === s.module}
              feedback={feedback[s.module]}
            />
          ))}
        </div>
      )}

      <EventsSection
        title="Webhook events with errors"
        description="Zoho push events that failed to process. Queue the matching pull to re-run them, or fix the record in Zoho."
        query={failed}
        emptyText="No webhook event has failed to process."
        showError
      />

      <EventsSection
        title="Recent webhook activity"
        description={`Latest 200 Zoho push events. Unprocessed right now: ${unprocessed.data?.length ?? 0}.`}
        query={recent}
        emptyText="No webhook has been received yet. Zoho notifications arrive at the zoho-webhook Edge Function once the webhook is registered in Zoho."
      />
    </div>
  );
}

function FeedbackLine({ feedback }: { feedback: Feedback }) {
  return (
    <p role={feedback.tone === 'error' ? 'alert' : 'status'} className={`text-xs break-words ${feedback.tone === 'error' ? 'text-error' : 'text-success'}`}>
      {feedback.text}
    </p>
  );
}

function ModuleCard({
  state,
  onSync,
  syncing,
  feedback,
}: {
  state: ZohoSyncStateRow;
  onSync: () => void;
  syncing: boolean;
  feedback?: Feedback;
}) {
  const hasPullTask = zohoPullTaskFor(state.module) !== null;
  return (
    <div className="rounded-lg border border-border bg-surface p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-text">{state.module}</h3>
        <RunStatusPill status={state.last_run_status} />
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-y-1 text-xs">
        <dt className="text-text-muted">Last pull</dt>
        <dd className="text-right text-text">{state.last_pull_at ? formatDateTime(state.last_pull_at) : 'Never'}</dd>
        <dt className="text-text-muted">Last push</dt>
        <dd className="text-right text-text">{state.last_push_at ? formatDateTime(state.last_push_at) : 'Never'}</dd>
        <dt className="text-text-muted">Records pulled</dt>
        <dd className="text-right text-text">{state.records_pulled}</dd>
        <dt className="text-text-muted">Records pushed</dt>
        <dd className="text-right text-text">{state.records_pushed}</dd>
      </dl>
      {state.last_run_error && (
        <p className="mt-2 text-xs text-error break-words">
          {redactCrmIdentifiers(state.last_run_error)}
        </p>
      )}
      <button
        type="button"
        onClick={onSync}
        disabled={syncing || !hasPullTask}
        title={hasPullTask ? undefined : `${state.module} has no pull task; it syncs by webhook only.`}
        className={`${BTN} mt-3 w-full border border-border bg-surface text-text hover:bg-surface-strong`}
      >
        {syncing ? 'Queuing…' : hasPullTask ? 'Pull now' : 'Webhook only'}
      </button>
      {!hasPullTask && <p className="mt-1 text-[11px] text-text-faint">{state.module} arrive by webhook; there is no scheduled pull for this module.</p>}
      {feedback && <div className="mt-2"><FeedbackLine feedback={feedback} /></div>}
    </div>
  );
}

function EventsSection({
  title,
  description,
  query,
  emptyText,
  showError = false,
}: {
  title: string;
  description: string;
  query: { data?: ZohoWebhookEventRow[]; isLoading: boolean; isError: boolean };
  emptyText: string;
  showError?: boolean;
}) {
  const rows = query.data ?? [];
  return (
    <section className="rounded-lg border border-border bg-surface">
      <header className="px-4 sm:px-5 py-3 border-b border-border">
        <h3 className="text-sm font-semibold text-text">{title}</h3>
        <p className="text-xs text-text-muted">{description}</p>
      </header>
      {query.isLoading ? (
        <p className="p-5 text-sm text-text-muted" role="status">Loading…</p>
      ) : query.isError ? (
        <p role="alert" className="p-5 text-sm text-notification">Could not load webhook events. Refresh to try again.</p>
      ) : rows.length === 0 ? (
        <p className="p-6 text-center text-sm text-text-muted">{emptyText}</p>
      ) : (
        <>
          {/* Mobile cards */}
          <ul className="md:hidden divide-y divide-border">
            {rows.map((e) => (
              <li key={e.id} className="p-4 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-text">{e.module} · {e.operation}</p>
                  <EventStatusPill processed={!!e.processed_at} hasError={!!e.process_error} />
                </div>
                <p className="text-xs text-text-muted">{formatDateTime(e.received_at)}</p>
                {showError && (
                  <p className="text-xs text-error break-words">{e.process_error ? redactCrmIdentifiers(e.process_error) : 'Unknown error'}</p>
                )}
              </li>
            ))}
          </ul>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-3 text-xs text-text-muted">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Module</th>
                  <th className="text-left px-4 py-2 font-medium">Operation</th>
                  <th className="text-left px-4 py-2 font-medium">{showError ? 'Error' : 'Status'}</th>
                  <th className="text-left px-4 py-2 font-medium">Received</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((e) => (
                  <tr key={e.id} className="align-top">
                    <td className="px-4 py-2 text-text">{e.module}</td>
                    <td className="px-4 py-2 text-text">{e.operation}</td>
                    <td className="px-4 py-2 text-text max-w-md break-words">
                      {showError ? (e.process_error ? redactCrmIdentifiers(e.process_error) : 'Unknown error') : (
                        <EventStatusPill processed={!!e.processed_at} hasError={!!e.process_error} />
                      )}
                    </td>
                    <td className="px-4 py-2 text-text-muted text-xs whitespace-nowrap">{formatDateTime(e.received_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function RunStatusPill({ status }: { status: ZohoSyncStateRow['last_run_status'] }) {
  if (!status) {
    return <span className="inline-block rounded px-2 py-0.5 text-xs font-medium bg-surface-3 text-text-muted">no run yet</span>;
  }
  const styles = {
    ok: 'bg-success-soft text-success',
    partial: 'bg-warning-soft text-warning',
    error: 'bg-error-soft text-error',
  } as const;
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${styles[status]}`}>
      {status}
    </span>
  );
}

function EventStatusPill({ processed, hasError }: { processed: boolean; hasError: boolean }) {
  if (hasError) {
    return <span className="inline-block rounded px-2 py-0.5 text-xs font-medium bg-error-soft text-error">error</span>;
  }
  if (processed) {
    return <span className="inline-block rounded px-2 py-0.5 text-xs font-medium bg-success-soft text-success">processed</span>;
  }
  return <span className="inline-block rounded px-2 py-0.5 text-xs font-medium bg-warning-soft text-warning">pending</span>;
}
